import type { ImageStyle, TextStyle, ViewStyle } from 'react-native';
import { theme, type Theme } from '@/theme';

type StyleValue = ViewStyle | TextStyle | ImageStyle;
type ColorKey = keyof Theme['colors'];
type SpacingKey = keyof Theme['spacing'];
type RadiusKey = keyof Theme['borderRadii'];

export type StylePropValue = StyleValue | StyleValue[] | null | undefined | false;

type CompatibilitySpacing = SpacingKey | number | string | undefined;
type CompatibilityColor = ColorKey | string | undefined;

export const baseTheme = theme;

export function resolveThemeColor(
  color: CompatibilityColor,
  activeTheme: Theme = baseTheme,
): string | undefined {
  if (color === undefined) {
    return undefined;
  }

  return (activeTheme.colors as Record<string, string>)[color] ?? color;
}

export function resolveSpacingValue(
  value: CompatibilitySpacing,
  activeTheme: Theme = baseTheme,
): number | string | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  const token = activeTheme.spacing[value as SpacingKey];
  return token ?? value;
}

export function resolveRadiusValue(
  value: RadiusKey | number | undefined,
  activeTheme: Theme = baseTheme,
): number | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  return activeTheme.borderRadii[value];
}

type CompatibilityProps = {
  margin?: CompatibilitySpacing;
  marginTop?: CompatibilitySpacing;
  marginRight?: CompatibilitySpacing;
  marginBottom?: CompatibilitySpacing;
  marginLeft?: CompatibilitySpacing;
  marginHorizontal?: CompatibilitySpacing;
  marginVertical?: CompatibilitySpacing;
  padding?: CompatibilitySpacing;
  paddingTop?: CompatibilitySpacing;
  paddingRight?: CompatibilitySpacing;
  paddingBottom?: CompatibilitySpacing;
  paddingLeft?: CompatibilitySpacing;
  paddingHorizontal?: CompatibilitySpacing;
  paddingVertical?: CompatibilitySpacing;
  gap?: CompatibilitySpacing;
  rowGap?: CompatibilitySpacing;
  columnGap?: CompatibilitySpacing;
  backgroundColor?: CompatibilityColor;
  color?: CompatibilityColor;
  borderColor?: CompatibilityColor;
  borderTopColor?: CompatibilityColor;
  borderRightColor?: CompatibilityColor;
  borderBottomColor?: CompatibilityColor;
  borderLeftColor?: CompatibilityColor;
  borderRadius?: RadiusKey | number;
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  flex?: number;
  flexBasis?: number | string;
  flexGrow?: number;
  flexShrink?: number;
  flexDirection?: ViewStyle['flexDirection'];
  flexWrap?: ViewStyle['flexWrap'];
  alignItems?: ViewStyle['alignItems'];
  alignSelf?: ViewStyle['alignSelf'];
  justifyContent?: ViewStyle['justifyContent'];
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  height?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  position?: ViewStyle['position'];
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
  overflow?: ViewStyle['overflow'];
  opacity?: number;
  shadowColor?: string;
  shadowOffset?: ViewStyle['shadowOffset'];
  shadowOpacity?: number;
  shadowRadius?: number;
  elevation?: number;
  textAlign?: TextStyle['textAlign'];
  fontWeight?: TextStyle['fontWeight'];
  fontSize?: number;
  lineHeight?: number;
  textDecorationLine?: TextStyle['textDecorationLine'];
};

export function resolveCompatibilityStyle(
  props: CompatibilityProps,
  activeTheme: Theme,
): StyleValue {
  return {
    margin: resolveSpacingValue(props.margin, activeTheme) as ViewStyle['margin'],
    marginTop: resolveSpacingValue(props.marginTop, activeTheme) as ViewStyle['marginTop'],
    marginRight: resolveSpacingValue(props.marginRight, activeTheme) as ViewStyle['marginRight'],
    marginBottom: resolveSpacingValue(props.marginBottom, activeTheme) as ViewStyle['marginBottom'],
    marginLeft: resolveSpacingValue(props.marginLeft, activeTheme) as ViewStyle['marginLeft'],
    marginHorizontal: resolveSpacingValue(
      props.marginHorizontal,
      activeTheme,
    ) as ViewStyle['marginHorizontal'],
    marginVertical: resolveSpacingValue(
      props.marginVertical,
      activeTheme,
    ) as ViewStyle['marginVertical'],
    padding: resolveSpacingValue(props.padding, activeTheme) as ViewStyle['padding'],
    paddingTop: resolveSpacingValue(props.paddingTop, activeTheme) as ViewStyle['paddingTop'],
    paddingRight: resolveSpacingValue(props.paddingRight, activeTheme) as ViewStyle['paddingRight'],
    paddingBottom: resolveSpacingValue(
      props.paddingBottom,
      activeTheme,
    ) as ViewStyle['paddingBottom'],
    paddingLeft: resolveSpacingValue(props.paddingLeft, activeTheme) as ViewStyle['paddingLeft'],
    paddingHorizontal: resolveSpacingValue(
      props.paddingHorizontal,
      activeTheme,
    ) as ViewStyle['paddingHorizontal'],
    paddingVertical: resolveSpacingValue(
      props.paddingVertical,
      activeTheme,
    ) as ViewStyle['paddingVertical'],
    gap: resolveSpacingValue(props.gap, activeTheme) as ViewStyle['gap'],
    rowGap: resolveSpacingValue(props.rowGap, activeTheme) as ViewStyle['rowGap'],
    columnGap: resolveSpacingValue(props.columnGap, activeTheme) as ViewStyle['columnGap'],
    backgroundColor: resolveThemeColor(props.backgroundColor, activeTheme),
    color: resolveThemeColor(props.color, activeTheme),
    borderColor: resolveThemeColor(props.borderColor, activeTheme),
    borderTopColor: resolveThemeColor(props.borderTopColor, activeTheme),
    borderRightColor: resolveThemeColor(props.borderRightColor, activeTheme),
    borderBottomColor: resolveThemeColor(props.borderBottomColor, activeTheme),
    borderLeftColor: resolveThemeColor(props.borderLeftColor, activeTheme),
    borderRadius: resolveRadiusValue(props.borderRadius, activeTheme),
    borderWidth: props.borderWidth,
    borderTopWidth: props.borderTopWidth,
    borderRightWidth: props.borderRightWidth,
    borderBottomWidth: props.borderBottomWidth,
    borderLeftWidth: props.borderLeftWidth,
    flex: props.flex,
    flexBasis: props.flexBasis as ViewStyle['flexBasis'],
    flexGrow: props.flexGrow,
    flexShrink: props.flexShrink,
    flexDirection: props.flexDirection,
    flexWrap: props.flexWrap,
    alignItems: props.alignItems,
    alignSelf: props.alignSelf,
    justifyContent: props.justifyContent,
    width: props.width as ViewStyle['width'],
    minWidth: props.minWidth as ViewStyle['minWidth'],
    maxWidth: props.maxWidth as ViewStyle['maxWidth'],
    height: props.height as ViewStyle['height'],
    minHeight: props.minHeight as ViewStyle['minHeight'],
    maxHeight: props.maxHeight as ViewStyle['maxHeight'],
    position: props.position,
    top: props.top as ViewStyle['top'],
    right: props.right as ViewStyle['right'],
    bottom: props.bottom as ViewStyle['bottom'],
    left: props.left as ViewStyle['left'],
    overflow: props.overflow,
    opacity: props.opacity,
    shadowColor: props.shadowColor,
    shadowOffset: props.shadowOffset,
    shadowOpacity: props.shadowOpacity,
    shadowRadius: props.shadowRadius,
    elevation: props.elevation,
    textAlign: props.textAlign,
    fontWeight: props.fontWeight,
    fontSize: props.fontSize,
    lineHeight: props.lineHeight,
    textDecorationLine: props.textDecorationLine,
  };
}
